import React, { PureComponent } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Button } from '@shiftfinancial/design-system';
import Grid from '@material-ui/core/Grid';
import { Typography, withStyles } from '@material-ui/core';
import { Field, Form, Formik } from 'formik';
import NumberFormat from 'react-number-format';

import StepContainer from '../steps/StepContainer';
import CompositeAlert from '../../core/CompositeAlert';
import Fieldset from '../../core/Fieldset';
import styles from './mobileverificationstyles';
import FormikPhoneNumberField from '../../core/formik/FormikPhoneNumberField';
import FormikNumberFormatInputField from '../../core/formik/FormikNumberFormatInputField';
import { loginCodeValidationSchema } from './validationSchema';
import { mobileValidationSchemaWithMandatory } from '../../core/mobileNumberValidationSchema';
import contexts from '../../../contexts';
import phoneNumberUtils from '../../../utils/phoneNumberUtils';
import { digitalRegistrationStepOneCompleteEvent } from '../../../utils/gtmUtils';

class MobileVerification extends PureComponent {
  constructor(props) {
    super(props);

    this.handleSendLoginCode = this.handleSendLoginCode.bind(this);
    this.handleLoginCodeSubmit = this.handleLoginCodeSubmit.bind(this);
  }

  static handleMobileNumberKeyPress(e) {
    const { which, keyCode, target } = e;
    const { value } = target;
    const charCode = which || keyCode;
    const char = String.fromCharCode(charCode);
    if (char === '0' && !value) {
      e.preventDefault();
    }
  }

  componentDidMount() {
    digitalRegistrationStepOneCompleteEvent();
    this.props.reset();
  }

  componentWillUnmount() {
    this.props.clearTimer();
  }

  handleSendLoginCode({ mobileNumber }, { setSubmitting }) {
    const callback = () => {
      setSubmitting(false);
    };
    this.props.sendLoginCode(mobileNumber, callback);
  }

  handleLoginCodeSubmit(values, { setSubmitting }) {
    this.props.resetValidation();

    const { loginCode } = values;
    if (loginCode) {
      const { user } = this.context;
      const callback = () => {
        setSubmitting(false);
      };
      this.props.confirmLoginCode(loginCode, user, callback);
    } else {
      setSubmitting(false);
    }
  }

  render() {
    const {
      classes,
      mobileNumber,
      codeExpiry,
      validationResult,
      loginCodeSendingFailed,
      message,
      canResendLoginCode,
      mobileNumberFromInvitation,
    } = this.props;

    let sendButtonLabel = 'Send verification code via SMS';
    if (mobileNumber && codeExpiry) {
      sendButtonLabel = canResendLoginCode ? 'Resend code' : 'Resend available in 1 minute...';
    }

    return (
      <Formik
        enableReinitialize
        initialValues={{
          loginCode: '',
        }}
        validateOnBlur={false}
        validationSchema={loginCodeValidationSchema}
        onSubmit={this.handleLoginCodeSubmit}
        render={({ submitForm, isSubmitting, dirty, values, errors, setFieldValue }) => {
          const isSubmitDisabled = isSubmitting || !dirty || Object.keys(errors).length > 0;
          const isSubmitVisible = codeExpiry !== null && codeExpiry !== undefined;
          const mobileDisplay = mobileNumberFromInvitation ? (
            phoneNumberUtils.maskMobilePhoneNumber(mobileNumberFromInvitation)
          ) : (
            <React.Fragment>
              +<NumberFormat value={mobileNumber} displayType="text" format="## ### ### ###" />
            </React.Fragment>
          );

          return (
            <StepContainer
              title="Verify your mobile number"
              subtitle={
                mobileNumberFromInvitation
                  ? `To confirm it's you, we'll send an SMS to your mobile ending in ${mobileDisplay}`
                  : 'What is your mobile number?'
              }
              isSubmitVisible={isSubmitVisible}
              isSubmitDisabled={isSubmitDisabled}
              submitButtonAdditionalCssClassName="ga-reg-mobile"
              onSubmit={submitForm}
              footerLeftChildren={
                mobileNumberFromInvitation ? (
                  <Typography variant="body2" className={classes.footerLeftText}>
                    Not your mobile number? To register with a different mobile number,{' '}
                    <Link className={classes.link} to={{ pathname: '/registration/business' }}>
                      click here
                    </Link>
                  </Typography>
                ) : null
              }
            >
              <Formik
                initialValues={{
                  mobileNumber:
                    phoneNumberUtils.formatMobilePhoneNumber(mobileNumberFromInvitation) || '',
                }}
                validateOnBlur={false}
                validationSchema={mobileValidationSchemaWithMandatory}
                onSubmit={(data, { setSubmitting }) => {
                  if (values.loginCode !== '') {
                    setFieldValue('loginCode', '');
                  }
                  this.handleSendLoginCode(data, { setSubmitting });
                }}
                enableReinitialize
                render={(childFormProps) => {
                  const isSendDisabled =
                    childFormProps.submitCount || mobileNumberFromInvitation
                      ? !canResendLoginCode
                      : childFormProps.isSubmitting ||
                        !childFormProps.dirty ||
                        Object.keys(childFormProps.errors).length > 0;

                  return (
                    <Form autoComplete="off" data-testid="mobileNumberForm">
                      <Fieldset disabled={isSubmitting}>
                        <Grid
                          container
                          spacing={2}
                          className={classes.mobileNumberFormContainer}
                          alignItems="center"
                        >
                          {!mobileNumberFromInvitation && (
                            <Grid item xs={12} sm={6}>
                              <Field
                                name="mobileNumber"
                                component={FormikPhoneNumberField}
                                onKeyPress={MobileVerification.handleMobileNumberKeyPress}
                                pattern="^4[0-9]{2} [0-9]{3} [0-9]{3}|^04[0-9]{2} [0-9]{3} [0-9]{3}"
                                format="### ### ###"
                                disabled={!canResendLoginCode}
                                value={childFormProps.values.mobileNumber}
                                autoFocus
                                showNoError
                                data-testid="mobileNumber"
                              />
                            </Grid>
                          )}
                          <Grid item xs={12} sm={6} xl={4}>
                            <Button
                              appearance="primary"
                              disabled={isSendDisabled}
                              data-testid="sendCode"
                              fullWidth
                              onClick={childFormProps.submitForm}
                            >
                              {sendButtonLabel}
                            </Button>
                          </Grid>
                        </Grid>
                      </Fieldset>
                    </Form>
                  );
                }}
              />
              {mobileNumber && codeExpiry && (
                <Form autoComplete="off" data-testid="codeForm">
                  <Fieldset disabled={isSubmitting}>
                    <Grid container spacing={1} className={classes.smsCodeFormContainer}>
                      <Grid item xs={12}>
                        <p className={classes.loginCodeLabel}>
                          A code has been sent to {mobileDisplay} via SMS. Please enter it below.
                          This code will expire in {codeExpiry / 60} minutes.
                        </p>
                      </Grid>
                      <Grid item xs={12} sm={6} xl={4}>
                        <Field
                          name="loginCode"
                          component={FormikNumberFormatInputField}
                          pattern="[0-9]{6}"
                          format="######"
                          variant="outlined"
                          disabled={validationResult.isMobileConfirmed}
                          value={values.loginCode}
                          autoFocus
                          showNoError
                          placeholder="Enter the 6-digit SMS code"
                          data-testid="loginCode"
                        />
                      </Grid>
                    </Grid>
                  </Fieldset>
                </Form>
              )}
              {(loginCodeSendingFailed || validationResult.isValidated) && message && (
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <CompositeAlert
                      variant={validationResult.isMobileConfirmed ? 'success' : 'error'}
                      message={message}
                      data-testid="alert"
                    />
                  </Grid>
                </Grid>
              )}
            </StepContainer>
          );
        }}
      />
    );
  }
}

MobileVerification.propTypes = {
  classes: PropTypes.object,
  sendLoginCode: PropTypes.func.isRequired,
  confirmLoginCode: PropTypes.func.isRequired,
  mobileNumber: PropTypes.string,
  codeExpiry: PropTypes.number,
  validationResult: PropTypes.object,
  resetValidation: PropTypes.func.isRequired,
  loginCodeSendingFailed: PropTypes.bool.isRequired,
  message: PropTypes.string,
  canResendLoginCode: PropTypes.bool.isRequired,
  clearTimer: PropTypes.func.isRequired,
  mobileNumberFromInvitation: PropTypes.string,
  reset: PropTypes.func.isRequired,
};

MobileVerification.defaultProps = {
  classes: {},
  mobileNumber: null,
  codeExpiry: null,
  validationResult: null,
  message: null,
  mobileNumberFromInvitation: null,
};

MobileVerification.contextType = contexts.LoginContext;

export default withStyles(styles)(MobileVerification);
